import { Injectable, Inject, signal, computed } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { 
  ColoresTema, 
  NombreTema, 
  TEMAS, 
  MAPA_VARIABLES_CSS,
  VARIABLES_CSS_LEGACY 
} from '@core/constants/tema.constants';
import { PALETA_COLORES } from '@core/constants/paleta-colores.constants';

const CLAVE_ALMACENAMIENTO_TEMA = 'app-tema';
const TEMA_POR_DEFECTO: NombreTema = 'deudor';

@Injectable({
  providedIn: 'root'
})
export class TemaService {
  public readonly nombreTema = computed(() => this.temaActual());
  public readonly coloresTema = computed(() => TEMAS[this.temaActual()]);
  public readonly colorPersonalizadoActivo = signal<string | null>(null);
  
  private readonly temaActual = signal<NombreTema>(TEMA_POR_DEFECTO);

  constructor(@Inject(DOCUMENT) private readonly document: Document) {
    this.inicializarTema();
  }

  public establecerTema(nombreTema: NombreTema): void {
    if (!this.esTemaValido(nombreTema)) {
      nombreTema = TEMA_POR_DEFECTO;
    }
    this.colorPersonalizadoActivo.set(null);
    this.limpiarVariablesHeader();
    this.temaActual.set(nombreTema);
    this.aplicarTema(nombreTema);
    this.guardarTema(nombreTema);
  }

  public aplicarColorPersonalizado(codigoColor: string): void {
    const colorPaleta = PALETA_COLORES.find(c => c.codigo === codigoColor);
    if (!colorPaleta) {
      return;
    }

    this.temaActual.set(TEMA_POR_DEFECTO);
    this.aplicarTema(TEMA_POR_DEFECTO);

    this.colorPersonalizadoActivo.set(codigoColor);
    this.aplicarVariablesHeader(colorPaleta.variantes.primary, colorPaleta.variantes.primaryHover);
  }

  public restablecerTemaDefecto(): void {
    this.colorPersonalizadoActivo.set(null);
    this.limpiarVariablesHeader();
  }

  public getTheme(): NombreTema {
    return this.temaActual();
  }

  public getThemeColors(): ColoresTema {
    return TEMAS[this.temaActual()];
  }

  public getAvailableThemes(): NombreTema[] {
    return Object.keys(TEMAS) as NombreTema[];
  }

  private inicializarTema(): void {
    const temaGuardado = this.cargarTemaGuardado();
    const temaAAplicar = temaGuardado || TEMA_POR_DEFECTO;
    this.temaActual.set(temaAAplicar);
    this.aplicarTema(temaAAplicar);
  }

  private aplicarTema(nombreTema: NombreTema): void {
    const colores = TEMAS[nombreTema];
    if (!colores) {
      return;
    }

    this.aplicarVariantesCss(colores);
  }

  private aplicarVariantesCss(colores: ColoresTema): void {
    const root = this.document.documentElement;

    Object.entries(MAPA_VARIABLES_CSS).forEach(([claveColor, variableCss]) => {
      const color = colores[claveColor as keyof ColoresTema];
      root.style.setProperty(variableCss, color);
    });

    Object.entries(VARIABLES_CSS_LEGACY).forEach(([variableCss, claveColor]) => {
      const color = colores[claveColor];
      root.style.setProperty(variableCss, color);
    });
  }

  private aplicarVariablesHeader(primary: string, primaryHover: string): void {
    const root = this.document.documentElement;
    root.style.setProperty('--header-primary', primary);
    root.style.setProperty('--header-primary-hover', primaryHover);
  }

  private limpiarVariablesHeader(): void {
    const root = this.document.documentElement;
    root.style.removeProperty('--header-primary');
    root.style.removeProperty('--header-primary-hover');
  }

  private guardarTema(nombreTema: NombreTema): void {
    sessionStorage.setItem(CLAVE_ALMACENAMIENTO_TEMA, nombreTema);
  }

  private cargarTemaGuardado(): NombreTema | null {
    try {
      const guardado = sessionStorage.getItem(CLAVE_ALMACENAMIENTO_TEMA);
      if (guardado && this.esTemaValido(guardado)) {
        return guardado as NombreTema;
      }
    } catch {
      return null;
    }
    return null;
  }

  private esTemaValido(nombreTema: string): boolean {
    return Object.keys(TEMAS).includes(nombreTema);
  }
}
